/* The landing page's globe: the universities (src/pages/landing/globe/schools.json, made by
   scripts/landing-globe.mjs from the planner's directory), the one nearest a spot, search, and how its facts read.
   Pure functions, unit-tested (test/unit/globe.test.ts); the drawing is globe-view.ts. */

export type LL = [number, number];

/* one university, short keys to keep the file small:
   i id (the planner's), n name, ci city (ca in Arabic), c country, s US state, a [lat, lon], y a year's cost in US
   dollars (tuition and living), k 1 when the tuition is the university's own (t, for the year ty), p 1 public,
   w world rank (QS 2027: "32", "=68", "701-710", "1401+"), r rank in its country, m acceptance rate (%, for my),
   z students, f founded, cc a community college */
export type School = {
  i: string; n: string; ci: string; ca?: string; c: string; s?: string; a: LL; y: number; p: 0 | 1;
  w?: string; r?: number; k?: 1; t?: number; ty?: string; m?: number; my?: string; z?: number; f?: number; cc?: 1;
};
export type GlobeData = { langs: Record<string, string[]>; schools: School[] };

const RAD = Math.PI / 180;

/* great-circle distance in km */
export function km(a: LL, b: LL): number {
  const dLat = (b[0] - a[0]) * RAD, dLon = (b[1] - a[1]) * RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * RAD) * Math.cos(b[0] * RAD) * Math.sin(dLon / 2) ** 2;
  return 12742 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/* the university nearest a spot; level distances go to the better ranked */
export function nearest(schools: readonly School[], at: LL): { s: School; d: number } | null {
  let best: School | null = null, bd = Infinity;
  for (const s of schools) {
    const d = km(at, s.a);
    if (d < bd - 0.01 || (Math.abs(d - bd) <= 0.01 && best && rankNum(s.w) < rankNum(best.w))) { bd = d; best = s; }
  }
  return best ? { s: best, d: bd } : null;
}

export const rankNum = (w?: string) => { const m = String(w ?? '').match(/\d+/); return m ? +m[0] : 99999; };
/* a band ("701-710", "1401+") rather than one place */
export const isBand = (w?: string) => /[-+]/.test(String(w ?? ''));

/* how many universities in each country have a rank there (for "#2 of 93") */
export function rankedCounts(schools: readonly School[]): Record<string, number> {
  const n: Record<string, number> = {};
  for (const s of schools) if (s.r) n[s.c] = (n[s.c] ?? 0) + 1;
  return n;
}

/* how hard it is to get in, in words: from the published acceptance rate, else from the world rank */
export type Selectivity = 'high' | 'mid' | 'some' | 'low' | 'open';
export function selectivity(s: School): Selectivity | null {
  if (s.cc) return 'open';   /* community colleges admit everyone who finished school */
  if (typeof s.m === 'number') return s.m < 15 ? 'high' : s.m < 40 ? 'mid' : s.m < 70 ? 'some' : 'low';
  if (!s.w) return null;
  const r = rankNum(s.w);
  return r <= 30 ? 'high' : r <= 200 ? 'mid' : r <= 600 ? 'some' : 'low';
}

/* lower case, without accents, for matching what people type */
export const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/* what the search box finds: universities by name, then cities, then countries (each with its best-ranked
   university). countryNames gives a country's names in the languages the visitor may type in. */
export type Hit = { s: School; kind: 'uni' | 'city' | 'country'; label: string };
export function search(schools: readonly School[], query: string, countryNames: (c: string) => string[], limit = 6): Hit[] {
  const q = fold(query);
  if (q.length < 2) return [];
  const hits: Hit[] = [], seen = new Set<string>();
  const best = (list: School[]) => list.slice().sort((a, b) => rankNum(a.w) - rankNum(b.w) || (b.z ?? 0) - (a.z ?? 0))[0];
  const push = (h: Hit) => { const key = h.kind + ':' + h.label; if (!seen.has(key) && hits.length < limit) { seen.add(key); hits.push(h); } };
  /* cities: whole-word starts first */
  const byCity = new Map<string, School[]>();
  for (const s of schools) {
    const key = s.c + '|' + (s.s ?? '') + '|' + s.ci;
    if ([s.ci, s.ca ?? ''].some(x => x && fold(x).startsWith(q))) (byCity.get(key) ?? byCity.set(key, []).get(key)!).push(s);
  }
  const cities = [...byCity.values()].sort((a, b) => b.length - a.length);
  for (const list of cities.slice(0, 3)) { const s = best(list); push({ s, kind: 'city', label: s.ci }); }
  for (const s of schools) if (fold(s.n).includes(q)) push({ s, kind: 'uni', label: s.n });
  const byCountry = new Map<string, School[]>();
  for (const s of schools) (byCountry.get(s.c) ?? byCountry.set(s.c, []).get(s.c)!).push(s);
  for (const [c, list] of byCountry) if (countryNames(c).some(n => n && fold(n).startsWith(q))) { const s = best(list); push({ s, kind: 'country', label: c }); }
  return hits;
}

/* "43.66° N · 79.40° W" */
export function coords(a: LL): string {
  const f = (v: number, pos: string, neg: string) => `${Math.abs(v).toFixed(2)}° ${v >= 0 ? pos : neg}`;
  return `${f(a[0], 'N', 'S')} · ${f(a[1], 'E', 'W')}`;
}

/* "$63K", "$9.5K", "$0" */
export function usdShort(n: number): string {
  if (n < 1000) return '$' + Math.round(n);
  const k = n / 1000;
  return '$' + (k < 10 ? (Math.round(k * 10) / 10).toString() : Math.round(k).toString()) + 'K';
}

/* the planner, starting with this university: its town picked and the university on the list */
export const plannerBegin = (s: School) => '/planner/#begin=' + encodeURIComponent(s.i);
/* "Let's begin": an account first (the visitor is signed out on the landing page), then the planner with the university */
export const beginHref = (s: School) => '/start?next=' + encodeURIComponent(plannerBegin(s));
/* the university a sign-up is starting with: the id in its next (/planner/#begin=<id>), if there is one */
export function beginId(next: string | null | undefined): string | null {
  const m = String(next ?? '').match(/^\/planner\/#begin=([a-z]{2}-[a-z0-9-]+)$/);
  return m ? m[1] : null;
}
